import { useQuery } from "@tanstack/react-query";

import type { BillingEstimate } from "features/subscriptions/types";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { Pricing } from "common/types";

async function workspaceEstimateCreationRequest(
  workspaceId: string,
  planId: string,
  addonId: string,
  quantity: number,
  type: string,
  couponId: string,
): Promise<BillingEstimate> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/estimates/create`,
    {
      type,
      items: [
        { id: planId },
        ...(quantity > 0 ? [{ id: addonId, quantity }] : []),
      ],
      coupon: couponId,
    },
  );
  return estimate;
}

async function customerEstimateCreationRequest(
  planPricingId: string,
  quantity: number,
  couponId: string,
): Promise<BillingEstimate> {
  const { estimate } = await post("subscriptions/estimates/create", {
    seat_count: quantity,
    item_price_id: planPricingId,
    coupon: couponId,
  });
  return estimate;
}

// if subscriptionId is not provided, this is a customer estimate
export default function useEstimateCreation({
  isCustomerSubscription,
  pricing,
  quantity,
  couponId,
  isEnabled = true,
}: {
  isCustomerSubscription: boolean;
  pricing: { addon: Pricing; plan: Pricing } | undefined;
  quantity: number;
  couponId: string;
  isEnabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const {
    data: estimateCreation,
    isFetching: isLoadingEstimateCreation,
    error: estimateCreationError,
  } = useQuery({
    queryKey: isCustomerSubscription
      ? CUSTOMER_QUERY_KEYS.subscriptionKeys.creationEstimate(
          pricing?.plan.id,
          quantity,
          couponId,
        )
      : getQueryKeys(workspaceId).subscriptionKeys.creationEstimate(
          pricing?.plan.id,
          quantity,
          couponId,
        ),
    queryFn: isCustomerSubscription
      ? () =>
          customerEstimateCreationRequest(pricing?.plan.id, quantity, couponId)
      : () =>
          workspaceEstimateCreationRequest(
            workspaceId,
            pricing?.plan.id,
            pricing?.addon.id,
            quantity,
            pricing?.plan.subscription_type,
            couponId,
          ),
    enabled: !!pricing && isEnabled,
    gcTime: 0,
  });

  return { estimateCreation, isLoadingEstimateCreation, estimateCreationError };
}
